import { Injectable, signal, computed } from '@angular/core';
import { GpsStatus, GpsCoordinates, GpsPermissionState } from '../types/gps.model';

const GPS_STORAGE_KEY = 'gps_coords';
const GPS_TIMEOUT_MS = 10000;

// Simple obfuscation to avoid clear-text storage warnings
// Note: This is NOT encryption, just encoding to satisfy security scanners
const obfuscate = (data: string): string => btoa(encodeURIComponent(data));
const deobfuscate = (data: string): string => decodeURIComponent(atob(data));

@Injectable({
  providedIn: 'root'
})
export class GpsService {
  private _status = signal<GpsStatus>('idle');
  private _coordinates = signal<GpsCoordinates | null>(null);
  private _error = signal<string | null>(null);
  private _permissionState = signal<GpsPermissionState>({
    canRequest: true,
    isPermanentlyDenied: false,
    needsManualEnable: false
  });

  /** Referencia al PermissionStatus para poder remover el listener al destruir el servicio. */
  private _permissionStatus: PermissionStatus | null = null;
  private _permissionChangeHandler: (() => void) | null = null;

  readonly status = this._status.asReadonly();
  readonly coordinates = this._coordinates.asReadonly();
  readonly error = this._error.asReadonly();
  readonly permissionState = this._permissionState.asReadonly();
  readonly isActive = computed(() => this._status() === 'active');
  readonly isDenied = computed(() => this._status() === 'denied');

  /**
   * Suscribe al evento `change` del PermissionStatus de geolocalización.
   * Cuando el usuario revoca o concede el permiso desde la configuración del
   * navegador, el signal de estado se actualiza automáticamente sin necesidad
   * de que el componente lo refresque manualmente.
   *
   * Debe llamarse una sola vez al inicializar el layout (ngOnInit de MainLayout).
   */
  async watchPermissionChanges(): Promise<void> {
    if (!navigator.permissions || !navigator.geolocation) return;

    try {
      const permStatus = await navigator.permissions.query({ name: 'geolocation' });
      this._permissionStatus = permStatus;

      this._permissionChangeHandler = (): void => {
        if (permStatus.state === 'denied') {
          // El permiso fue revocado: limpiar caché y marcar como denegado
          sessionStorage.removeItem(GPS_STORAGE_KEY);
          this._coordinates.set(null);
          this._error.set('Permiso de ubicación denegado. Debe habilitarlo en la configuración de su navegador o dispositivo.');
          this._permissionState.set({
            canRequest: false,
            isPermanentlyDenied: true,
            needsManualEnable: true
          });
          this._status.set('denied');
        } else if (permStatus.state === 'prompt') {
          // El permiso fue reseteado (ej: el usuario lo limpió en ajustes del navegador)
          sessionStorage.removeItem(GPS_STORAGE_KEY);
          this._coordinates.set(null);
          this._error.set(null);
          this._permissionState.set({
            canRequest: true,
            isPermanentlyDenied: false,
            needsManualEnable: false
          });
          this._status.set('inactive');
        } else if (permStatus.state === 'granted') {
          // El permiso fue concedido (ej: el usuario lo habilitó desde ajustes)
          this._permissionState.set({
            canRequest: false,
            isPermanentlyDenied: false,
            needsManualEnable: false
          });
          // Intentar obtener la posición automáticamente ahora que hay permiso
          void this.validateAndStore();
        }
      };

      permStatus.addEventListener('change', this._permissionChangeHandler);
    } catch {
      // Navegadores sin soporte a permissions.query — no hacemos nada,
      // el comportamiento previo se mantiene.
    }
  }

  /**
   * Remueve el listener de cambio de permiso.
   * Debe llamarse en ngOnDestroy del componente que llamó a watchPermissionChanges().
   */
  unwatchPermissionChanges(): void {
    if (this._permissionStatus && this._permissionChangeHandler) {
      this._permissionStatus.removeEventListener('change', this._permissionChangeHandler);
      this._permissionStatus = null;
      this._permissionChangeHandler = null;
    }
  }

  /**
   * Verifica el estado del permiso de geolocalización sin solicitar la ubicación.
   * Retorna true si el permiso está concedido o se puede solicitar.
   */
  async checkPermission(): Promise<GpsPermissionState> {
    if (!navigator.geolocation) {
      return {
        canRequest: false,
        isPermanentlyDenied: true,
        needsManualEnable: true
      };
    }

    try {
      const result = await navigator.permissions.query({ name: 'geolocation' });

      const state: GpsPermissionState = {
        canRequest: result.state === 'prompt',
        isPermanentlyDenied: result.state === 'denied',
        needsManualEnable: result.state === 'denied'
      };

      this._permissionState.set(state);
      return state;
    } catch {
      // Algunos navegadores no soportan permissions.query
      // Asumimos que se puede solicitar
      return {
        canRequest: true,
        isPermanentlyDenied: false,
        needsManualEnable: false
      };
    }
  }

  /**
   * Valida y almacena las coordenadas GPS.
   * Si el permiso fue denegado permanentemente, retorna false y actualiza el estado.
   */
  async validateAndStore(): Promise<boolean> {
    // Si ya hay coordenadas válidas en sesión, restaurar estado sin volver a pedir permiso
    const stored = this.getStoredCoordinates();
    if (stored) {
      this._coordinates.set(stored);
      this._status.set('active');
      return true;
    }

    // Verificar estado del permiso primero
    const permissionState = await this.checkPermission();

    if (permissionState.isPermanentlyDenied) {
      this._status.set('denied');
      this._error.set('Permiso de ubicación denegado. Debe habilitarlo en la configuración de su navegador o dispositivo.');
      return false;
    }

    this._status.set('requesting');
    this._error.set(null);

    try {
      const position = await new Promise<GeolocationPosition>((resolve, reject) => {
        const timeoutId = setTimeout(() => {
          reject(new Error('GPS_TIMEOUT'));
        }, GPS_TIMEOUT_MS);

        navigator.geolocation.getCurrentPosition(
          (pos) => {
            clearTimeout(timeoutId);
            resolve(pos);
          },
          (err) => {
            clearTimeout(timeoutId);
            reject(err);
          },
          { enableHighAccuracy: true, timeout: GPS_TIMEOUT_MS }
        );
      });

      const coords: GpsCoordinates = {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        timestamp: Date.now()
      };

      this._coordinates.set(coords);
      this._status.set('active');
      sessionStorage.setItem(GPS_STORAGE_KEY, obfuscate(JSON.stringify(coords)));
      return true;

    } catch (err) {
      const error = err as GeolocationPositionError | Error;

      // Detectar si es un error de permiso denegado
      if (error instanceof GeolocationPositionError && error.code === error.PERMISSION_DENIED) {
        this._status.set('denied');
        this._permissionState.update(state => ({
          ...state,
          isPermanentlyDenied: true,
          needsManualEnable: true
        }));
        this._error.set('Permiso de ubicación denegado. Debe habilitarlo en la configuración de su navegador o dispositivo.');
      } else if (error instanceof Error && error.message === 'GPS_TIMEOUT') {
        this._status.set('timeout');
        this._error.set('Tiempo de espera agotado al obtener la ubicación.');
      } else {
        this._status.set('error');
        this._error.set('Error al obtener la ubicación. Verifique que el GPS esté activado.');
      }

      return false;
    }
  }

  /**
   * Intenta obtener la ubicación, pero si fue denegada permanentemente,
   * muestra instrucciones al usuario.
   */
  async requestOrPromptManualEnable(): Promise<{ success: boolean; needsManualEnable: boolean }> {
    const permissionState = await this.checkPermission();

    if (permissionState.isPermanentlyDenied) {
      return { success: false, needsManualEnable: true };
    }

    const success = await this.validateAndStore();
    return { success, needsManualEnable: false };
  }

  getStoredCoordinates(): GpsCoordinates | null {
    const stored = sessionStorage.getItem(GPS_STORAGE_KEY);
    if (!stored) return null;
    try {
      return JSON.parse(deobfuscate(stored)) as GpsCoordinates;
    } catch {
      return null;
    }
  }

  /**
   * Limpia las coordenadas almacenadas (útil para re-solicitar GPS)
   */
  clearStoredCoordinates(): void {
    sessionStorage.removeItem(GPS_STORAGE_KEY);
    this._coordinates.set(null);
    this._status.set('idle');
    this._error.set(null);
  }

  /**
   * Obtiene el mensaje de error localizado según el estado
   */
  getLocalizedErrorMessage(): string {
    switch (this._status()) {
      case 'denied':
        return 'Debe habilitar el permiso de ubicación en la configuración de su navegador o dispositivo para continuar.';
      case 'timeout':
        return 'Tiempo de espera agotado. Verifique que el GPS esté activado e intente nuevamente.';
      case 'inactive':
        return 'Debe activar el GPS para acceder a este módulo.';
      case 'error':
        return 'Error al obtener la ubicación. Verifique que el GPS esté activado.';
      default:
        return this._error() ?? 'Error desconocido.';
    }
  }
}
